import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { CheckCircle2, Calendar, CreditCard } from 'lucide-react';
import { format } from 'date-fns';
import { bookingsAPI } from '../services/api';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get the correct image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=400';
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  if (imagePath.startsWith('/api/uploads/')) {
    return `${BACKEND_URL}${imagePath}`;
  }
  if (imagePath.startsWith('/uploads/')) {
    return `${BACKEND_URL}/api${imagePath}`;
  }
  return imagePath;
};

const BookingConfirmPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { property, checkIn, checkOut, guests, pricing } = location.state || {};
  const [loading, setLoading] = useState(false);

  if (!property || !checkIn || !checkOut) {
    navigate('/');
    return null;
  }

  const handleConfirmBooking = async () => {
    try {
      setLoading(true);
      
      const bookingData = {
        propertyId: property._id || property.id,
        checkIn: new Date(checkIn).toISOString(),
        checkOut: new Date(checkOut).toISOString(),
        guests,
        pricing,
        guestInfo: {
          name: 'Guest User',
          email: 'guest@geostays.com',
          phone: '+91-9876543210'
        }
      };
      
      const response = await bookingsAPI.create(bookingData);
      
      if (response.success) {
        toast.success('Booking confirmed successfully!');
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Failed to confirm booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const totalGuests = guests.adults + guests.children + guests.infants;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            ← Back
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Confirm and pay</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Booking Details */}
          <div className="space-y-6">
            {/* Trip Details Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Calendar className="h-5 w-5 text-[#7CB342]" />
                  <span>Your trip</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-semibold mb-1">Dates</div>
                    <div className="text-gray-600">
                      {format(checkIn, 'MMM dd')} - {format(checkOut, 'MMM dd, yyyy')}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="text-[#7CB342]">Edit</Button>
                </div>
                <Separator />
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-semibold mb-1">Guests</div>
                    <div className="text-gray-600">{totalGuests} guest{totalGuests !== 1 ? 's' : ''}</div>
                  </div>
                  <Button variant="ghost" size="sm" className="text-[#7CB342]">Edit</Button>
                </div>
              </CardContent>
            </Card>

            {/* Payment Method Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <CreditCard className="h-5 w-5 text-[#7CB342]" />
                  <span>Payment method</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="border-2 border-[#7CB342] rounded-lg p-4 bg-green-50">
                    <div className="flex items-center space-x-3">
                      <CheckCircle2 className="h-5 w-5 text-[#7CB342]" />
                      <div>
                        <div className="font-semibold">Pay with card</div>
                        <div className="text-sm text-gray-600">Visa, Mastercard, Amex</div>
                      </div>
                    </div>
                  </div>
                  <div className="border rounded-lg p-4 opacity-50 cursor-not-allowed">
                    <div className="flex items-center space-x-3">
                      <div className="h-5 w-5 rounded-full border-2" />
                      <div>
                        <div className="font-semibold">UPI</div>
                        <div className="text-sm text-gray-600">Coming soon</div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cancellation Policy */}
            <Card>
              <CardHeader>
                <CardTitle>Cancellation policy</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm mb-3">
                  Free cancellation before 48 hours of check-in. After that, cancel before check-in and get a 50% refund.
                </p>
                <Button variant="link" className="text-[#7CB342] p-0 h-auto">Learn more</Button>
              </CardContent>
            </Card>

            {/* Ground Rules */}
            <Card>
              <CardHeader>
                <CardTitle>Ground rules</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm mb-4">
                  We ask every guest to remember a few simple things about what makes a great guest.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• Follow the house rules</li>
                  <li>• Treat your host's home like your own</li>
                  <li>• Communicate with your host</li>
                </ul>
              </CardContent>
            </Card>

            {/* Confirm Button */}
            <Button
              onClick={handleConfirmBooking}
              disabled={loading}
              className="w-full py-6 text-base font-semibold bg-[#7CB342] hover:bg-[#6da036] text-white rounded-lg disabled:opacity-50"
            >
              {loading ? 'Processing...' : 'Confirm and pay'}
            </Button>
          </div>

          {/* Right Column - Property Summary */}
          <div>
            <Card className="sticky top-24">
              <CardContent className="p-6">
                {/* Property Image and Title */}
                <div className="flex space-x-4 mb-6">
                  <img
                    src={getImageUrl(property.images[0])}
                    alt={property.title}
                    className="w-32 h-24 object-cover rounded-lg"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=400';
                    }}
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold mb-1">{property.title}</h3>
                    <p className="text-sm text-gray-600 mb-2">{property.type}</p>
                    <div className="flex items-center space-x-1 text-sm">
                      <span className="font-medium">★ {property.rating}</span>
                      <span className="text-gray-500">({property.reviews} reviews)</span>
                    </div>
                  </div>
                </div>

                <Separator className="my-6" />

                {/* Price Breakdown */}
                <div>
                  <h3 className="font-semibold text-lg mb-4">Price details</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span>₹{property.price.toLocaleString()} x {pricing.nights} nights</span>
                      <span>₹{pricing.subtotal.toLocaleString()}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between font-semibold text-lg">
                      <span>Total (INR)</span>
                      <span>₹{pricing.total.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmPage;